import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Database, Share2, MessageSquare, AlertTriangle, Zap, TrendingUp, TrendingDown, ArrowRight, Activity, Plus, Play, Search, Clock, Bookmark, Shield } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('dqp_token')}` } });

const fadeUp = { hidden: { opacity: 0, y: 15 }, visible: { opacity: 1, y: 0, transition: { duration: 0.4 } } };
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

function SparkLine({ color = '#5b63ea' }) {
  const points = Array.from({ length: 12 }, (_, i) => ({ x: i * 8, y: 20 + Math.sin(i * 0.8) * 10 + Math.random() * 6 }));
  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
  return (
    <svg width="88" height="40" viewBox="0 0 88 40" className="opacity-60">
      <path d={path} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const SIGNAL_COLORS = { critical: '#ef4444', high: '#f97316', medium: '#f59e0b', low: '#3b82f6' };
const ACTION_ICONS = {
  source_connected: Database, query_run: MessageSquare, query_saved: Bookmark,
  signal_approved: Shield, signal_rejected: AlertTriangle, signal_escalated: AlertTriangle,
};

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [activities, setActivities] = useState([]);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/dashboard/stats`, authH());
      setStats(data);
    } catch {}
  }, []);

  const loadActivity = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/dashboard/activity`, authH());
      setActivities(data);
    } catch {}
  }, []);

  useEffect(() => { loadStats(); loadActivity(); }, [loadStats, loadActivity]);

  const kpis = [
    { label: 'Connected Sources', value: stats?.connected_sources ?? 0, trend: stats?.connected_sources > 0 ? '+1' : null, trendUp: true, color: '#5b63ea', icon: Database },
    { label: 'Tables Discovered', value: stats?.tables_discovered ?? 0, trend: stats?.tables_discovered > 0 ? `+${stats.tables_discovered}` : null, trendUp: true, color: '#3b82f6', icon: Share2 },
    { label: 'Relationships Found', value: stats?.relationships_found ?? 0, trend: stats?.relationships_found > 0 ? `+${stats.relationships_found}` : null, trendUp: true, color: '#10b981', icon: Activity },
    { label: 'Intel Detected', value: stats?.signals_detected ?? 0, trend: stats?.signals_detected > 0 ? `${stats.signals_detected}` : null, trendUp: false, color: '#f59e0b', icon: AlertTriangle },
    { label: 'Queries Run', value: stats?.queries_run ?? 0, trend: stats?.queries_run > 0 ? `${stats.queries_run}` : null, trendUp: true, color: '#8b5cf6', icon: MessageSquare },
    { label: 'Semantic Coverage', value: `${stats?.semantic_coverage ?? 0}%`, trend: stats?.semantic_coverage > 0 ? 'Active' : null, trendUp: true, color: '#06b6d4', icon: Zap },
  ];

  const signalChartData = stats?.signals_by_severity
    ? Object.entries(stats.signals_by_severity).filter(([, v]) => v > 0).map(([name, value]) => ({ name, value }))
    : [];

  const timeAgo = (ts) => {
    if (!ts) return '';
    const diff = Date.now() - new Date(ts).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8">
        <motion.div initial="hidden" animate="visible" variants={stagger}>
          <motion.div variants={fadeUp} className="mb-8">
            <h1 data-testid="dashboard-welcome" className="text-2xl font-bold text-[#1e1e2e]" style={{ fontFamily: 'Outfit' }}>
              Welcome back{user?.name ? `, ${user.name}` : ''}
            </h1>
            <p className="text-[#6b7280] text-sm mt-1">
              {user?.role === 'admin' ? 'Administrator' : 'Data Analyst'} &middot; Here is your workspace overview
            </p>
          </motion.div>

          <motion.div variants={fadeUp} data-testid="dashboard-metrics" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {kpis.map((kpi, i) => (
              <motion.div key={i} variants={fadeUp} data-testid={`metric-card-${i}`} className="bg-white rounded-xl border border-[#e6e8f2] p-5 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${kpi.color}12` }}>
                    <kpi.icon size={18} style={{ color: kpi.color }} />
                  </div>
                  <SparkLine color={kpi.color} />
                </div>
                <p className="text-2xl font-bold text-[#1e1e2e]" style={{ fontFamily: 'Outfit' }}>{kpi.value}</p>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs text-[#9ca3af]">{kpi.label}</span>
                  {kpi.trend && (
                    <span className={`text-[10px] font-medium flex items-center gap-0.5 ${kpi.trendUp ? 'text-emerald-600' : 'text-amber-600'}`}>
                      {kpi.trendUp ? <TrendingUp size={10} /> : <TrendingDown size={10} />} {kpi.trend}
                    </span>
                  )}
                </div>
              </motion.div>
            ))}
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Quick Actions */}
            <motion.div variants={fadeUp} className="lg:col-span-2 space-y-6">
              <div className="bg-white rounded-xl border border-[#e6e8f2] p-6">
                <h2 className="font-semibold text-[#1e1e2e] mb-4" style={{ fontFamily: 'Outfit' }}>Quick Actions</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { icon: Plus, title: 'Connect Data Source', desc: 'Link your databases and warehouses', to: '/app/data-sources', color: '#5b63ea' },
                    { icon: Play, title: 'Run Discovery', desc: 'Auto-discover schema relationships', to: '/app/semantic-graph', color: '#10b981' },
                    { icon: Search, title: 'Ask DQP', desc: 'Query your data in natural language', to: '/app/chat', color: '#8b5cf6' },
                    { icon: AlertTriangle, title: 'Review Intel', desc: 'Review data quality alerts', to: '/app/signals', color: '#f59e0b' },
                  ].map((action, i) => (
                    <Link key={i} to={action.to}>
                      <div data-testid={`quick-action-${i}`} className="flex items-start gap-3 p-4 rounded-xl border border-[#e6e8f2] hover:border-[#5b63ea]/30 hover:shadow-sm cursor-pointer transition-all group">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${action.color}12` }}>
                          <action.icon size={16} style={{ color: action.color }} />
                        </div>
                        <div className="min-w-0">
                          <h3 className="text-sm font-medium text-[#1e1e2e] group-hover:text-[#5b63ea] transition-colors">{action.title}</h3>
                          <p className="text-xs text-[#9ca3af] mt-0.5">{action.desc}</p>
                        </div>
                        <ArrowRight size={14} className="ml-auto mt-1 text-[#d4d8e6] group-hover:text-[#5b63ea] transition-colors flex-shrink-0" />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Intel Overview Donut */}
              {signalChartData.length > 0 && (
                <div className="bg-white rounded-xl border border-[#e6e8f2] p-6">
                  <h2 className="font-semibold text-[#1e1e2e] mb-3" style={{ fontFamily: 'Outfit' }}>Intel by Severity</h2>
                  <div className="flex items-center gap-6">
                    <div className="w-[140px] h-[140px]" style={{ minWidth: 140, minHeight: 140 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={signalChartData} cx="50%" cy="50%" innerRadius={35} outerRadius={60} paddingAngle={3} dataKey="value">
                            {signalChartData.map((entry, i) => <Cell key={i} fill={SIGNAL_COLORS[entry.name] || '#5b63ea'} />)}
                          </Pie>
                          <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e6e8f2', fontSize: 12 }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="space-y-2">
                      {signalChartData.map(s => (
                        <div key={s.name} className="flex items-center gap-2 text-xs">
                          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: SIGNAL_COLORS[s.name] }} />
                          <span className="text-[#6b7280] capitalize">{s.name}</span>
                          <span className="font-semibold text-[#1e1e2e]">{s.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </motion.div>

            {/* Activity Feed */}
            <motion.div variants={fadeUp}>
              <div className="bg-white rounded-xl border border-[#e6e8f2] p-6">
                <h2 className="font-semibold text-[#1e1e2e] mb-4" style={{ fontFamily: 'Outfit' }}>Recent Activity</h2>
                <div className="space-y-3">
                  {activities.length === 0 ? (
                    <p className="text-xs text-[#9ca3af] text-center py-6">No activity yet. Start by connecting a data source.</p>
                  ) : (
                    activities.slice(0, 10).map((item, i) => {
                      const Icon = ACTION_ICONS[item.action] || Activity;
                      const color = item.action.includes('signal') ? '#f59e0b' : item.action.includes('query') ? '#8b5cf6' : '#5b63ea';
                      return (
                        <div key={i} data-testid={`activity-${i}`} className="flex items-start gap-3">
                          <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}12` }}>
                            <Icon size={12} style={{ color }} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs text-[#1e1e2e] font-medium capitalize">{item.action.replace(/_/g, ' ')}</p>
                            <p className="text-[10px] text-[#9ca3af] mt-0.5 truncate">{item.details}</p>
                          </div>
                          <span className="text-[10px] text-[#9ca3af] flex-shrink-0">{timeAgo(item.timestamp)}</span>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </DashboardLayout>
  );
}
